import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import CtrlService from 'shared/services/CtrlService';
import {
  getCredential,
  createCredential,
  updateCredential,
  deleteCredential,
  getCredentialData,
  setCredentialData,
  addCredential,
  updateCredentialInState,
  removeCredential,
  setLoading,
  setError
} from './credentials.redux';
import {
  ICreateCredentialPayload,
  ICredential,
  IUpdateCredentialPayload
} from 'shared/interfaces/ctrlCredentials.interface';
import { openNotificationMessage } from 'shared/utils/NotificationMessages';
import { NotificationMessages } from 'shared/enums';

function* getCredentialSaga(): Generator<any, void, any> {
  try {
    yield put(getCredentialData());
    const credential: ICredential = yield call([CtrlService, 'getCredential']);
    yield put(setCredentialData(credential));
  } catch (error: any) {
    yield put(setError(error.message || 'Failed to fetch credential'));
  }
}

function* createCredentialSaga(
  action: PayloadAction<ICreateCredentialPayload>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const newCredential: ICredential = yield call(
      CtrlService.create,
      action.payload
    );
    yield put(addCredential(newCredential));
    yield put(setLoading(false));

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Credential was successfully created.'
    });
  } catch (error: any) {
    yield put(setLoading(false));
    yield put(setError(error.message || 'Failed to create credential'));
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Failed to create credential'
    });
  }
}

function* updateCredentialSaga(
  action: PayloadAction<IUpdateCredentialPayload>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const updatedCredential: ICredential = yield call(
      [CtrlService, 'update'],
      action.payload
    );
    yield put(updateCredentialInState(updatedCredential));
    yield put(setLoading(false));

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Credential was successfully updated.'
    });
  } catch (error: any) {
    yield put(setLoading(false));
    yield put(setError(error.message || 'Failed to update credential'));
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Failed to update credential'
    });
  }
}

function* deleteCredentialSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield call([CtrlService, 'delete']);
    yield put(removeCredential());
    yield put(setLoading(false));

    yield openNotificationMessage({
      type: NotificationMessages.Success,
      title: 'Success',
      message: 'Credential deleted successfully.'
    });
  } catch (error: any) {
    yield put(setLoading(false));
    yield put(setError(error.message || 'Failed to delete credential'));
    yield openNotificationMessage({
      type: NotificationMessages.Error,
      title: 'Error',
      message: error?.response?.data?.message || 'Failed to delete credential'
    });
  }
}

export default function* credentialsSaga() {
  yield takeEvery(getCredential.type, getCredentialSaga);
  yield takeEvery(createCredential.type, createCredentialSaga);
  yield takeEvery(updateCredential.type, updateCredentialSaga);
  yield takeEvery(deleteCredential.type, deleteCredentialSaga);
}
